import { z } from 'zod';
import { CURRENCIES } from '../money/money';
import { idSchema, moneySchema } from './common.schemas';

/**
 * Invoice model (PRD → Invoicing and collections; gitaumoses4/chukua#128). The header holds only the states
 * it passes through before it is locked: `sent` and `acknowledged` happen after issue, when the header can no
 * longer change, so they are `invoice_events` rows and `deriveInvoiceStatus` joins them back on.
 */
export const INVOICE_STATES = ['draft', 'pending_approval', 'approved', 'issued'] as const;
export type InvoiceState = (typeof INVOICE_STATES)[number];

export const INVOICE_PROGRESS_KINDS = ['sent', 'acknowledged'] as const;
export type InvoiceProgressKind = (typeof INVOICE_PROGRESS_KINDS)[number];

export const INVOICE_STATUSES = [...INVOICE_STATES, ...INVOICE_PROGRESS_KINDS] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

export const INVOICE_GROUPINGS = ['per_job', 'per_trip', 'per_customer_period'] as const;
export type InvoiceGrouping = (typeof INVOICE_GROUPINGS)[number];

export const INVOICE_LINE_SOURCES = ['trip', 'rate_card', 'accessorial', 'detention_decision', 'manual'] as const;
export type InvoiceLineSource = (typeof INVOICE_LINE_SOURCES)[number];

export const VAT_TREATMENTS = ['standard', 'zero_rated', 'exempt'] as const;
export type VatTreatment = (typeof VAT_TREATMENTS)[number];

/** Rates are basis points (1600 = 16%) so no float ever reaches a money calculation. */
const vatRateBpsSchema = z.number().int().min(0).max(10_000);

const nonEmptyText = (max: number) => z.string().trim().min(1).max(max);

type LineProblem = [path: string, message: string];
interface LineEvidenceFields {
  source: InvoiceLineSource;
  evidenceRef?: string;
  rateLineId?: string;
}

function evidenceProblems(line: LineEvidenceFields): LineProblem[] {
  if (line.source === 'manual') return line.evidenceRef === undefined ? [] : [['evidenceRef', 'A manual line has no evidence reference']];
  if (line.evidenceRef === undefined) return [['evidenceRef', 'Evidence reference is required']];
  if (line.source !== 'rate_card') return line.rateLineId === undefined ? [] : [['rateLineId', 'Only a rate card line stores a rate line']];
  if (line.rateLineId === undefined) return [['rateLineId', 'A rate card line stores the rate line it priced from']];
  return line.evidenceRef === line.rateLineId ? [] : [['evidenceRef', 'A rate card line’s evidence is its rate line']];
}

function vatProblems(line: { vatTreatment: VatTreatment; vatRateBps: number; vatAmountMinor: number }): LineProblem[] {
  const isUntaxed = line.vatTreatment !== 'standard';
  return isUntaxed && (line.vatRateBps !== 0 || line.vatAmountMinor !== 0) ? [['vatRateBps', 'Zero-rated and exempt lines carry no VAT']] : [];
}

const invoiceLineProblems = (line: LineEvidenceFields & Parameters<typeof vatProblems>[0]): LineProblem[] => [...evidenceProblems(line), ...vatProblems(line)];

export const invoiceLineInputSchema = z
  .object({
    description: nonEmptyText(500),
    quantity: z.number().nonnegative().multipleOf(0.001),
    unit: nonEmptyText(40),
    rateMinor: z.number().int().nonnegative(),
    amountMinor: z.number().int().nonnegative(),
    vatTreatment: z.enum(VAT_TREATMENTS),
    vatRateBps: vatRateBpsSchema,
    vatAmountMinor: z.number().int().nonnegative(),
    source: z.enum(INVOICE_LINE_SOURCES),
    /** The trip, accessorial or detention decision the line was priced from; absent on a manual line. */
    evidenceRef: nonEmptyText(200).optional(),
    /** The rate line (the rate card's version) that priced it. A plain id, never a live link to the card. */
    rateLineId: z.string().uuid().optional(),
  })
  .superRefine((line, ctx) => {
    for (const [path, message] of invoiceLineProblems(line)) ctx.addIssue({ code: 'custom', path: [path], message });
  });
export type InvoiceLineInput = z.infer<typeof invoiceLineInputSchema>;

/** Totals are derived from the lines, so a request never supplies them. */
export const invoiceHeaderInputSchema = z
  .object({
    customerId: idSchema,
    currency: z.enum(CURRENCIES),
    invoiceDate: z.string().date(),
    dueDate: z.string().date(),
    poNumber: nonEmptyText(100).optional(),
    groupingBasis: z.enum(INVOICE_GROUPINGS),
  })
  .refine((header) => header.dueDate >= header.invoiceDate, { message: 'Due date cannot be before the invoice date', path: ['dueDate'] });
export type InvoiceHeaderInput = z.infer<typeof invoiceHeaderInputSchema>;

export const invoiceEventInputSchema = z.object({
  kind: z.enum(INVOICE_PROGRESS_KINDS),
  occurredAt: z.string().datetime(),
});
export type InvoiceEventInput = z.infer<typeof invoiceEventInputSchema>;

export interface InvoiceLineView extends Omit<InvoiceLineInput, 'evidenceRef' | 'rateLineId'> {
  id: string;
  evidenceRef: string | null;
  rateLineId: string | null;
}

export interface InvoiceView {
  id: string;
  customerId: string;
  status: InvoiceStatus;
  invoiceNumber: string | null;
  currency: InvoiceHeaderInput['currency'];
  invoiceDate: string;
  dueDate: string;
  poNumber: string | null;
  groupingBasis: InvoiceGrouping;
  net: z.infer<typeof moneySchema>;
  vat: z.infer<typeof moneySchema>;
  gross: z.infer<typeof moneySchema>;
  issuedAt: string | null;
  lines: InvoiceLineView[];
}
